import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useEffect, useState } from "react";
import { Alert, Button, Icon, Loading, Modal, Typography } from "@/components/atoms";
import { AddCardForm } from "@/components/organisms/Onboarding/AddCardForm";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator } from "@/hooks";
import { StripePaymentMethod } from "@/redux/apis/payment/stripe/types";
import { useGetProviderPaymentMethodsQuery, useUpdateProviderPaymentMethodMutation } from "@/redux/apis/subscription/subscriptionPlansApi";
import { addToast } from "@/utils";

interface ChangePaymentMethodModalProps {
  open: boolean;
  onClose: () => void;
  initialShowAddCard?: boolean;
}

// Helper to get card brand icon name
const _getCardBrandIcon = (brand: string): string => {
  const brandIcons: Record<string, string> = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "Amex",
    discover: "Discover",
  };
  return brandIcons[brand.toLowerCase()] || "Card";
};

// Helper to format card brand for display
const formatCardBrand = (brand: string): string => {
  return brand.charAt(0).toUpperCase() + brand.slice(1);
};

interface PaymentMethodCardProps {
  paymentMethod: StripePaymentMethod;
  onSetDefault: () => void;
  isSettingDefault: boolean;
}

const PaymentMethodCard = ({ paymentMethod, onSetDefault, isSettingDefault }: PaymentMethodCardProps) => {
  const { card, isDefault } = paymentMethod;

  return (
    <div className="border-neutral-light-gray flex w-full items-center gap-3 rounded-lg border p-4">
      <Icon name="Card" size="lg" className="text-neutral-dark-gray" />
      <div className="flex flex-1 flex-col">
        <Typography variant="paragraph">
          {formatCardBrand(card.brand)} ending in {card.last4}
        </Typography>
        <Typography variant="small" className="text-neutral-dark-gray">
          Expires {card.expMonth.toString().padStart(2, "0")}/{card.expYear}
        </Typography>
      </div>
      {!isDefault && (
        <Button variant="tertiary" size="sm" onClick={onSetDefault} disabled={isSettingDefault}>
          {isSettingDefault ? "Setting..." : "Set as Default"}
        </Button>
      )}
    </div>
  );
};

export const ChangePaymentMethodModal = ({ open, onClose, initialShowAddCard = false }: ChangePaymentMethodModalProps) => {
  const operator = useOperator();
  const [showAddCard, setShowAddCard] = useState(initialShowAddCard);
  const [error, setError] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);

  // Sync showAddCard when modal opens with initialShowAddCard=true
  // (useState only captures the initial value on first render)
  useEffect(() => {
    if (open && initialShowAddCard) {
      setShowAddCard(true);
    }
  }, [open, initialShowAddCard]);

  const {
    data: paymentMethodsData,
    isLoading: isLoadingPaymentMethods,
    refetch,
  } = useGetProviderPaymentMethodsQuery({ providerId: operator.id });

  const [updatePaymentMethod] = useUpdateProviderPaymentMethodMutation();

  const paymentMethods = paymentMethodsData || [];

  const handleCancel = () => {
    setShowAddCard(false);
    setError(null);
    onClose();
  };

  const handleSetDefault = async (paymentMethodId: string) => {
    setSettingDefaultId(paymentMethodId);
    setError(null);

    try {
      await updatePaymentMethod({
        providerId: operator.id,
        paymentMethodId,
        isDefault: true,
      }).unwrap();

      addToast("success", "Default payment method updated");
    } catch (err) {
      const errors = getErrorMessages(err as FetchBaseQueryError | SerializedError);
      setError(errors[0] || "Failed to update payment method");
    } finally {
      setSettingDefaultId(null);
    }
  };

  const handleCardAdded = async (paymentMethodId: string) => {
    setShowAddCard(false);

    try {
      await updatePaymentMethod({
        providerId: operator.id,
        paymentMethodId: paymentMethodId,
        isDefault: true,
      }).unwrap();

      addToast("success", "Payment method added and set as default");
      refetch();
    } catch (err) {
      const errors = getErrorMessages(err as FetchBaseQueryError | SerializedError);
      setError(errors[0] || "Failed to set payment method as default");
    }
  };

  const handleAddCardCancel = () => {
    setShowAddCard(false);
  };

  return (
    <Modal open={open} onClose={handleCancel} className="w-[85vw] md:w-[500px] lg:w-[500px]">
      <div className="flex flex-col">
        {/* Title and Description */}
        <div className="flex flex-col gap-2">
          <Typography variant="title">Change Payment Method</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            {showAddCard ? "Fill up the credit card information" : "Select a payment method or add a new one"}
          </Typography>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mt-4">
            <Alert type="danger" message={error} />
          </div>
        )}

        {/* Content Section */}
        <div className="flex flex-col gap-4 py-6">
          {isLoadingPaymentMethods ? (
            <div className="relative min-h-[150px]">
              <Loading className="rounded-lg" />
            </div>
          ) : showAddCard ? (
            <AddCardForm providerId={operator.id} onCardAdded={handleCardAdded} onCancel={handleAddCardCancel} />
          ) : (
            <>
              {/* Existing Payment Methods */}
              {paymentMethods.length > 0 && (
                <div className="flex flex-col gap-3">
                  {paymentMethods.map((pm) => (
                    <PaymentMethodCard
                      key={pm.paymentMethodId}
                      paymentMethod={pm}
                      onSetDefault={() => handleSetDefault(pm.paymentMethodId)}
                      isSettingDefault={settingDefaultId === pm.paymentMethodId}
                    />
                  ))}
                </div>
              )}

              {/* Add New Card Button */}
              <button
                type="button"
                onClick={() => setShowAddCard(true)}
                className="flex w-full items-center gap-3 rounded-lg border border-dashed border-neutral-dark-gray p-4 text-left transition-colors hover:bg-neutral-surface-gray"
              >
                <Icon name="Add" size="lg" className="text-primary" />
                <Typography variant="paragraph" className="text-primary">
                  Add new payment method
                </Typography>
              </button>
            </>
          )}
        </div>

        {/* Action Buttons - Only show when not adding a card (AddCardForm has its own buttons) */}
        {!showAddCard && (
          <div className="mt-8 flex flex-row justify-end">
            <Button type="button" size="lg" variant="secondary" onClick={handleCancel}>
              Close
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};
